/**
 * Workshops API — services, orders, POS booking
 */
import apiClient from './client';

export interface WorkshopServiceTemplate {
    id: number;
    workshop_store: number;
    category?: number | null;
    name: string;
    name_ar?: string;
    description?: string;
    description_ar?: string;
    price: string | number;
    duration_minutes?: number;
    is_active?: boolean;
    workshop_name?: string;
}

export const workshopsApi = {
    // Workshop stores (public listing)
    getWorkshops: async (params?: { country_code?: string; search?: string }) => {
        const { data } = await apiClient.get('/workshops/public/', { params });
        return data;
    },

    getWorkshop: async (id: number | string) => {
        const { data } = await apiClient.get(`/workshops/public/${id}/`);
        return data;
    },

    getCategories: async () => {
        const { data } = await apiClient.get('/workshops/categories/');
        return data;
    },

    getServices: async (params?: { country_code?: string; workshop_store?: number | string; category?: number | string }) => {
        const { data } = await apiClient.get('/workshops/services/', {
            params,
            skipAuth: true,
        } as any);
        return data;
    },

    // Workshop orders
    getOrders: async (params?: object) => {
        const { data } = await apiClient.get('/workshops/orders/', { params });
        return data;
    },

    getOrder: async (id: number | string) => {
        const { data } = await apiClient.get(`/workshops/orders/${id}/`);
        return data;
    },

    createOrder: async (payload: {
        service: number;
        notes?: string;
        payment_method?: 'cash' | 'card' | 'bank';
        advance_amount?: string | number;
    }) => {
        const { data } = await apiClient.post('/workshops/orders/', payload);
        return data;
    },

    // POS workshop order (vendor only)
    posCreate: async (payload: {
        customer_name: string;
        customer_phone?: string;
        payment_method: string;
        deposit_amount?: number;
        notes?: string;
        items: Array<{
            line_type: 'service' | 'part' | 'addon';
            service_id?: number;
            part_variant_id?: number;
            description: string;
            quantity: number;
            unit_price: number;
            discount_percent?: number;
        }>;
    }) => {
        const { data } = await apiClient.post('/workshops/orders/pos-create/', payload);
        return data;
    },

    // Car history / maintenance tracking
    getCars: async (params?: object) => {
        const { data } = await apiClient.get('/workshops/cars/', { params });
        return data;
    },

    getCar: async (id: number | string) => {
        const { data } = await apiClient.get(`/workshops/cars/${id}/`);
        return data;
    },

    createCar: async (payload: object) => {
        const { data } = await apiClient.post('/workshops/cars/', payload);
        return data;
    },

    updateCar: async (id: number | string, payload: object) => {
        const { data } = await apiClient.patch(`/workshops/cars/${id}/`, payload);
        return data;
    },

    deleteCar: async (id: number | string) => {
        const { data } = await apiClient.delete(`/workshops/cars/${id}/`);
        return data;
    },

    getCarTimeline: async (id: number | string) => {
        const { data } = await apiClient.get(`/workshops/cars/${id}/timeline/`);
        return data;
    },

    getCarByCode: async (code: string) => {
        const { data } = await apiClient.get('/workshops/cars/by-code/', { params: { code } });
        return data;
    },

    getCarMessages: async (carId: number | string, params?: object) => {
        const { data } = await apiClient.get(`/workshops/cars/${carId}/messages/`, { params });
        return data;
    },

    sendCarMessage: async (carId: number | string, payload: { message: string; record?: number | string }) => {
        const { data } = await apiClient.post(`/workshops/cars/${carId}/messages/`, payload);
        return data;
    },

    getCarRecords: async (params?: object) => {
        const { data } = await apiClient.get('/workshops/car-records/', { params });
        return data;
    },

    createCarRecord: async (payload: object | FormData) => {
        const isFormData = payload instanceof FormData;
        const { data } = await apiClient.post(
            '/workshops/car-records/',
            payload,
            isFormData ? { headers: { 'Content-Type': 'multipart/form-data' } } : undefined
        );
        return data;
    },

    updateCarRecord: async (id: number | string, payload: object | FormData) => {
        const isFormData = payload instanceof FormData;
        const { data } = await apiClient.patch(
            `/workshops/car-records/${id}/`,
            payload,
            isFormData ? { headers: { 'Content-Type': 'multipart/form-data' } } : undefined
        );
        return data;
    },

    getCarReminders: async (params?: object) => {
        const { data } = await apiClient.get('/workshops/car-reminders/', { params });
        return data;
    },

    updateCarReminder: async (id: number | string, payload: object) => {
        const { data } = await apiClient.patch(`/workshops/car-reminders/${id}/`, payload);
        return data;
    },
};

/**
 * Settings API — feature toggles, shipping, payments (admin)
 */
export const settingsApi = {
    getPublic: async () => {
        const { data } = await apiClient.get('/settings/public/');
        return data;
    },

    getConfig: async () => {
        const { data } = await apiClient.get('/settings/config/');
        return data;
    },

    setConfig: async (key: string, value: string) => {
        const { data } = await apiClient.put(`/settings/config/${key}/`, { value });
        return data;
    },

    // Payment providers
    getPaymentProviders: async () => {
        const { data } = await apiClient.get('/payments/providers/');
        return data;
    },

    updatePaymentProvider: async (id: number, payload: object) => {
        const { data } = await apiClient.patch(`/payments/providers/${id}/`, payload);
        return data;
    },

    togglePaymentProvider: async (id: number) => {
        const { data } = await apiClient.post(`/payments/providers/${id}/toggle/`);
        return data;
    },

    // Shipping methods
    getShippingMethods: async () => {
        const { data } = await apiClient.get('/shipping/methods/');
        return data;
    },

    createShippingMethod: async (payload: object) => {
        const { data } = await apiClient.post('/shipping/methods/', payload);
        return data;
    },

    updateShippingMethod: async (id: number, payload: object) => {
        const { data } = await apiClient.patch(`/shipping/methods/${id}/`, payload);
        return data;
    },
};

/**
 * Admin API — vendor approvals, orders, refunds
 */
export const adminApi = {
    // Vendor management
    getVendors: async (params?: object) => {
        const { data } = await apiClient.get('/auth/stores/', { params });
        return data;
    },

    approveVendor: async (storeId: number) => {
        const { data } = await apiClient.post(`/auth/stores/${storeId}/approve/`);
        return data;
    },

    rejectVendor: async (storeId: number, reason: string) => {
        const { data } = await apiClient.post(`/auth/stores/${storeId}/reject/`, { reason });
        return data;
    },

    getPOSRequests: async () => {
        const { data } = await apiClient.get('/auth/pos-requests/');
        return data;
    },

    approvePOSRequest: async (id: number, note?: string) => {
        const { data } = await apiClient.post(`/auth/pos-requests/${id}/approve/`, { note });
        return data;
    },

    // Orders
    getOrders: async (params?: object) => {
        const { data } = await apiClient.get('/sales/orders/', { params });
        return data;
    },

    updateOrderStatus: async (id: number, status: string) => {
        const { data } = await apiClient.post(`/sales/orders/${id}/update-status/`, { status });
        return data;
    },

    // Refunds
    getRefunds: async () => {
        const { data } = await apiClient.get('/sales/refunds/');
        return data;
    },

    processRefund: async (id: number) => {
        const { data } = await apiClient.post(`/sales/refunds/${id}/process/`);
        return data;
    },

    // Marketplace moderation
    getPendingListings: async () => {
        const { data } = await apiClient.get('/marketplace/listings/', { params: { status: 'pending' } });
        return data;
    },

    // Documents (PDF download URLs)
    getSalesInvoiceUrl: (orderId: number) => `/api/documents/sales/${orderId}/invoice/`,
    getSalesReceiptUrl: (orderId: number) => `/api/documents/sales/${orderId}/receipt/`,
    getRefundNoteUrl: (refundId: number) => `/api/documents/sales/refund/${refundId}/note/`,
    getWorkshopInvoiceUrl: (orderId: number) => `/api/documents/workshop/${orderId}/invoice/`,
};
